import ChatService from '@/services/ChatService.js';
import ContactService from '@/services/ContactService.js';
import ReminderService from '@/services/ReminderService.js';
import { whatsappLogo, FexIALogo } from '@/config';
import { getFileType, wpDialogTime, wpMessageTime } from '@/utils/utils';
import { encryptStorage } from '@/utils/storage';
import { processMessages } from './conversation';
import { isStickerFileName } from '@/modules/pbxCloud/components/Chat/emoji-picker/stickers.js';

// FEX-1192: el sticker viaja como una imagen más; en el listado se nombra por
// su prefijo (lastMsj / la URL del adjunto lo conservan) en vez de "Imagen".
export const STICKER_PREVIEW = '<sticker> Sticker';

export function processSipmovilMessages(messages, reverse=true, isFexIA=false) {
  const userName = encryptStorage.getItem('user').Username
  for (let index = 0; index < messages.length; index++) {
    // const timeMsg = messages[index].time
    messages[index].time = wpMessageTime(
      `${messages[index].time}T00:00:00-05:00`
    );
    const messageData = messages[index].data
    if (reverse) messageData.reverse();
    for (
      let indexMessage = 0;
      indexMessage < messageData.length;
      indexMessage++
    ) {
      const message = messageData[indexMessage];
      message.created_at = message.timestamp;
      // FEX-1124: estado en el historial. read (✓✓ dorado) > delivered (✓✓ negro) > sent (✓).
      // Un mensaje persistido en BD siempre fue enviado, así que el mínimo es 'sent'
      // (mensajes históricos previos al despliegue pueden traer sent:false aunque sí
      // se enviaron). El error solo llega en vivo por `message_send_error`.
      message.status = message.readedMsj
        ? 'read'
        : (message.delivered ? 'delivered' : 'sent');
      message.payload = {
        type: message.kind,
        text: message.text,
      };
      if (!message.kind || message.kind === "NO_ATTACHED") {
        messages[index].data[indexMessage].message = message.content;
      } else {
        message.file_message = message.url;
        messages[index].data[indexMessage].message = message.url;
      }
      message.direction = 'in';
      if (message.author[0] === userName && !message.fexIA_response) {
        message.direction = 'out';
      }
      // FexIA: mis mensajes se consideran ENTREGADOS (la IA no notifica entrega). El backend
      // trae delivered:false, así que sin esto caerían a ✓ al reabrir/recargar el chat.
      if (isFexIA && message.direction === 'out' && message.status !== 'read') {
        message.status = 'delivered';
      }
      // Normalizar readed_by: puede llegar como string ",249" o "249,307"
      if (message.readed_by !== undefined && message.readed_by !== null) {
        if (!Array.isArray(message.readed_by)) {
          message.readed_by = String(message.readed_by)
            .split(',')
            .filter(Boolean)
            .map(Number);
        }
      } else {
        message.readed_by = [];
      }
      // Reacciones (task_reacciones): reactions = [{ emoji, users:[userId,...] }].
      // Se preserva lo que envíe el backend a futuro; por ahora se garantiza el array.
      if (!Array.isArray(message.reactions)) message.reactions = [];
    }
  }
  return messages;
}

function processSenders(senders) {
  const dialogs = [];
  for (let index = 0; index < senders.length; index++) {
    const sender = senders[index];
    if (sender.origin) console.log('[processSenders] origin encontrado:', sender.id, sender.origin);
    // const dateMessage = new Date(sender.date_message)
    let image = whatsappLogo;
    let add = '';
    if (sender.app === 'whatsapp' && !sender.blocked) {
      image = whatsappLogo;
    } else if (sender.blocked) {
      add = ' (Banned)';
    }
    const dialog = {
      id: sender.id,
      contact_dialog: (sender.custom_name || sender.name) + add,
      last_message: sender.last_message,
      last_message_direction: sender.last_message_direction,
      date_message: wpDialogTime(new Date(sender.date_message)),
      message_type: sender.message_type,
      is_group: false,
      img_dialog: image,
      owner: sender.owner,
      user_session: sender.user,
      
      phone: sender.phone,
      bsuid: sender.bsuid,
      username: sender.username,
      destination: sender.phone || sender.bsuid,
      displayIdentifier: sender.phone || (sender.username ? `@${sender.username}` : (sender.bsuid || '')),
      app_name: sender.app_name,
      app: sender.app,
      label: sender.label || { color: '#ffffff' },
      unread: sender.unread,
      blocked: sender.blocked,
      state: sender.state,
      isIn: sender.last_message_direction === 'in' ? true : false,
      draft: '',
      writing: false,
      writingAgent: '',
      origin: sender.origin || '',
    };
    if (sender.last_message !== "" && sender.last_message.includes(".")) {
      const fileObj = getFileType(sender.last_message.split(".").at(-1))
      if (fileObj.isImage) dialog.last_message = `<${fileObj.fileIcon}> Imagen`
      else if (fileObj.isAudio) dialog.last_message = `<${fileObj.fileIcon}> Audio`
      else if (fileObj.isVideo) dialog.last_message = `<${fileObj.fileIcon}> Video`
      else if (fileObj.isOther && !fileObj.isUnknown) dialog.last_message = `<${fileObj.fileIcon}> Archivo`
    }
    dialogs.push(dialog);
  }
  return dialogs;
}

function processSipmovilSenders(senders) {
  const dialogs = [];
  const user = encryptStorage.getItem('user')
  for (let index = 0; index < senders.length; index++) {
    const sender = senders[index];
    processSipmovilMessages(sender.chats)
    const dialog = {
      id: sender.id,
      account: sender.account,
      chatId: sender.chatId,
      getInfoName: sender.userName,
      contact_dialog: sender.ext !== "000" ? `${sender.name} - ${sender.ext}` : sender.name,
      last_message: sender.lastMsj,
      // FEX-1124: read > delivered > sent(unread). El backend debe enviar `deliveredLastMsj`
      // (booleano, análogo a `readedLastMsj`) para poder distinguir enviado de entregado al
      // recargar. Hoy no llega ese campo (el `received` que manda es constante "Leído"), así
      // que sin él cae a 'unread' (✓) — el ✓✓ entregado no persiste hasta que backend lo agregue.
      last_message_status: sender.readedLastMsj === true
        ? 'read'
        : (sender.deliveredLastMsj === true ? 'delivered' : 'unread'),
      last_message_incoming: sender.typeMsj === 'received' ? true : false,
      date_message: wpDialogTime(new Date(sender.dayMsj)),
      img_dialog: sender.ext !== "000" ? `https://${user.PbxServer}${sender.img}` : FexIALogo,
      destination: sender.ext,
      unread: sender.unread,
      state: sender.state,
      chats: sender.chats,
      isGroup: sender.isGroup,
      writing: false,
      isActive: sender.isActive,
      isActiveMember: sender.is_active_member ?? true,
      exitReason: sender.exit_reason ?? null,
      description: sender.description,
      deletedLastMessage: sender.deletedMsj,
      idLastMessage: sender.idLastMsj,
      isFexIA: sender.isFexIA,
      project: sender.project,
      project_id: sender.project_id,
      session_id: sender.session_id
    };
    if (dialog.isGroup) {
      dialog.nameLastMessage = sender.last_message.nameLastMessage;
      dialog.contact_dialog = sender.contact.name;
      dialog.img_dialog = `https://${user.PbxServer}${sender.contact.picture}`;
      dialog.state = "online";
      dialog.onlyAdminsMessages = sender.onlyGroupsAdmin;
    }
    if (sender.lastMsj !== "" && sender.lastMsj.includes(".")) {
      const fileObj = getFileType(sender.lastMsj.split(".").at(-1))
      if (fileObj.isImage && isStickerFileName(sender.lastMsj)) dialog.last_message = STICKER_PREVIEW
      else if (fileObj.isImage) dialog.last_message = `<${fileObj.fileIcon}> Imagen`
      else if (fileObj.isAudio) dialog.last_message = `<${fileObj.fileIcon}> Audio`
      else if (fileObj.isVideo) dialog.last_message = `<${fileObj.fileIcon}> Video`
      else if (fileObj.isOther && !fileObj.isUnknown) dialog.last_message = `<${fileObj.fileIcon}> Archivo`
    }
    dialogs.push(dialog);
  }
  return dialogs;
}

const chat = {
  namespaced: true,
  state: {
    chats: [],
    sipmovilChats: [],
    sipmovilChatsForward: [],
    recentChatGroupCreatedId: null,
    filteredChats: false,
    renderSipmovilChats: [],
    filteredSipmovilChats: false,
    stringfilter: '',
    stringJoinFilter: '',
    chatsTotal: 0,
    openedChat: {},
    page: 1,
    recordChange: false,
    originOpenedChat: '',
    lastSipmovilMessages: false,
    totalSipmovilMessages: 0,
    totalUnreadSipmovilChats: 0,
    totalUnreadJoinChats: 0,
    totalUnreadChats: 0,
    foundChats: [],
    foundJoinChats: [],
    stringLabels: '',
    selectedOrigins: [],
    labelPage: 1,
    noReplyPage: 1,
    unreadPage: 1,
    orderButton: false,
    stringAgents: '',
    agentPage: 1,
    blockScroll: false,
    filterUnread: false,
    editDelete: false,
    endButton: false,
    allMessagesInView: false,
    orderByState: false,
    archived: false,
    banned: false,
    bulkSelectionMode: false,
    selectedChatIds: [],
    bulkSelectAll: false,
    bulkDateFrom: null,
    bulkDateTo: null,
    bulkDateCriteria: null,
    bulkDownloads: [],
    transferSelectionMode: false,
    selectedTransferChatIds: [],
    transferPreviousChat: null,
    transferSelectionBusy: false,
    transferDateFrom: null,
    transferDateTo: null,
    contextMenuChat: {
      menuVisible: false,
      menuPosition: {
        top: '0px',
        left: '0px',
      },
      chatId: null,
    },
    joinContactName: '',
    closingTypes: [],
    filterChatsOnlineStatus: false,
    // Búsqueda dentro del chat
    searchInChatActive: false,
    searchInChatQuery: '',
    searchInChatResults: [],
    searchInChatCurrentIndex: 0,
    preserveSearchOnNavigation: false,
    fexia_session_id: '',
    chatReminders: {},
    pendingReminderNavigation: null,
    pendingJoinTabActivation: false,
    // FC-550: petición para que el widget flotante abra una conversación de Join
    // desde fuera del módulo de chat (hoy: botón WhatsApp del detalle de
    // interacción del CRM). Forma: { phone, name }. Mismo patrón que
    // pendingReminderNavigation, pero consumido por FloatingChat.vue.
    pendingWidgetChat: null,
  },
  mutations: {
    SET_CHAT_REMINDER(state, { senderId, reminder }) {
      state.chatReminders = { ...state.chatReminders, [senderId]: reminder };
    },
    REMOVE_CHAT_REMINDER(state, senderId) {
      const updated = { ...state.chatReminders };
      delete updated[senderId];
      state.chatReminders = updated;
    },
    SET_PENDING_REMINDER_NAV(state, senderId) {
      state.pendingReminderNavigation = senderId;
    },
    SET_PENDING_JOIN_TAB(state, val) {
      state.pendingJoinTabActivation = val;
    },
    SET_PENDING_WIDGET_CHAT(state, payload) {
      state.pendingWidgetChat = payload;
    },
    // Inserta un chat de Join en el listado si todavía no está. Necesario antes
    // de `selectChat`: SET_CHAT hace splice(chatIndex, 1, chat) y con
    // chatIndex === -1 reemplazaría el ÚLTIMO chat de la lista.
    ADD_CHAT_IF_MISSING(state, chat) {
      const exists = state.chats.some((x) => x.id === chat.id);
      if (!exists) state.chats.unshift(chat);
    },
    SET_RECENT_CHAT_GROUP_CREATED_ID(state, chatId) {
      state.recentChatGroupCreatedId = chatId;
    },
    SET_FEXIA_SESSION_ID(state, sessionId) {
      state.fexia_session_id = sessionId
    },
    SET_OPENEDCHAT_NAME(state, name) {
      state.openedChat.contact_dialog = name;
    },
    SET_JOIN_CONTACT_NAME(state, name) {
      state.joinContactName = name;
    },
    SET_CHAT_PIN(state, chatId) {
      state.contextMenuChat.chatId = chatId;
    },
    SHOW_CONTEXT_MENU(state, event) {
      // Establecer la posición del menú donde ocurrió el clic derecho
      state.contextMenuChat.menuPosition.top = `${event.clientY}px`;
      state.contextMenuChat.menuPosition.left = `${event.clientX}px`;

      // Mostrar el menú
      state.contextMenuChat.menuVisible = true;
    },
    HIDE_CONTEXT_MENU(state) {
      state.contextMenuChat.menuVisible = false;
    },
    SHOW_EDIT_DELETE(state) {
      state.editDelete = !state.editDelete;
    },
    TOGGLE_END_BUTTON(state, val) {
      state.endButton = val;
    },
    FILTER_UNREAD(state, val) {
      state.filterUnread = val;
    },
    STOP_RECORDING(state) {
      state.recordChange = !state.recordChange;
    },
    LOAD_CHATS(state, chats) {
      state.chats = state.chats.concat(chats);
    },
    LOAD_SIPMOVIL_CHATS(state, chats) {
      if (state.preserveSearchOnNavigation &&
          chats === state.renderSipmovilChats &&
          state.sipmovilChats !== state.renderSipmovilChats) {
        return;
      }
      state.sipmovilChats = chats;
    },
    LOAD_SIPMOVIL_CHATS_FORDWARD(state, chats) {
      state.sipmovilChatsForward = chats;
    },
    LOAD_RENDER_SIPMOVIL_CHATS(state, chats) {
      state.renderSipmovilChats = chats;
    },
    SET_FILTERED_CHATS(state, [value, string]) {
      if (state.preserveSearchOnNavigation && string === '' && state.stringfilter !== '') {
        return;
      }

      state.filteredSipmovilChats = value;
      state.stringfilter = string;
    },
    SET_ONLINE_FILTERED_CHATS(state, chats) {
      state.filteredChatsOnline = chats;
    },
    TOGGLE_FILTER_CHAT_STATUS(state, value) {
      state.filterChatsOnlineStatus = value;
    },
    SET_JOIN_FILTERED_CHATS(state, [value, string]) {
      state.filteredChats = value;
      state.stringJoinFilter = string;
    },
    SET_ALL_MESSAGES_IN_VIEW(state, value) {
      state.allMessagesInView = value;
    },
    SET_STRING_LABELS(state, value) {
      state.stringLabels = value;
    },
    SET_STRING_AGENTS(state, value) {
      state.stringAgents = value;
    },
    SET_SELECTED_ORIGINS(state, value) {
      state.selectedOrigins = value;
    },
    SET_CHATS(state, chats) {
      state.chats = chats;
    },
    SET_JOIN_CHATS(state, chats) {
      state.chats = processSenders(chats);
    },
    SET_CHATS_TOTAL(state, chatsTotal) {
      state.chatsTotal = chatsTotal;
    },
    SET_CHAT(state, chat) {
      chat.unread = 0;
      state.openedChat = chat;
      const chatIndex = state.chats.findIndex((x) => x.id === chat.id, -1);
      state.chats.splice(chatIndex, 1, chat);
    },
    SET_SIPMOVIL_CHAT(state, chat) {
      chat.unread = 0;
      state.openedChat = chat;
      // const chatIndex = state.chats.findIndex((x) => x.id === chat.id, -1);
      // state.sipmovilChats.splice(chatIndex, 1, chat);
    },
    SET_SIPMOVIL_LAST(state, status) {
      state.lastSipmovilMessages = status;
    },
    SET_SIPMOVIL_TOTAL(state, total) {
      state.totalSipmovilMessages = total;
    },
    SET_NEW_CHAT(state, chat) {
      chat.unread = 0;
      chat.contact_dialog = chat.name;
      chat.destination = chat.phone;
      chat.label = { color: '#ffffff' };
      chat.img_dialog = whatsappLogo;
      state.chats.splice(0, 0, chat);
      state.openedChat = chat;
      // const chatIndex = state.chats.findIndex((x) => x.id === chat.id, -1);
    },
    SET_NEW_SIPMOVIL_CHAT(state, chat) {
      const chatIndex = state.sipmovilChats.findIndex((x) => x.id === chat.id, -1);
      state.chats.splice(chatIndex, 1, chat);
    },
    SET_FEXIA_NEW_CHAT(state, data) {
      const chat = {
        chatId: data.chat_id,
        id: data.chat_id,
        unread: 0,
        chats: [],
        contact_dialog: "FexIA " + data.name,
        img_dialog: FexIALogo,
        isFexIA: true,
        last_message: '',
        state: 'online',
        isGroup: false,
        writing: false,
        project: data.project_name || data.name,
        project_id: Number(data.project_id) || data.project_id,
      };
      const existingIndex = state.sipmovilChats.findIndex((c) => c.chatId === data.chat_id && c.isFexIA);
      if (existingIndex !== -1) {
        state.sipmovilChats.splice(existingIndex, 1, chat);
      } else {
        state.sipmovilChats.splice(0, 0, chat);
      }
      state.openedChat = chat;
    },
    ADD_FEXIA_SHARED_CHAT(state, data) {
      const already = state.sipmovilChats.some((c) => c.chatId === data.chat_id);
      if (already) return;
      const chat = {
        chatId: data.chat_id,
        id: data.chat_id,
        unread: 0,
        chats: [],
        contact_dialog: 'FexIA ' + data.name,
        img_dialog: FexIALogo,
        isFexIA: true,
        last_message: '',
        state: 'online',
        isGroup: false,
        writing: false,
        project: data.name,
        project_id: Number(data.project_id) || data.project_id,
      };
      state.sipmovilChats.splice(0, 0, chat);
    },
    UPDATE_LAST_MESSAGE(
      state,
      { chatId, messageContent, direction, messageDate, currentChat, isFiltered }
    ) {
      var chatIndex = state.chats.findIndex((x) => x.id === chatId, -1);
      var chat = state.chats[chatIndex];
      if (chat !== undefined) {
        // Si el contenido es una URL/ruta de archivo, formatear como icono + tipo
        // (Imagen / Audio / Video / Archivo) para evitar mostrar la URL cruda.
        // Es la misma heurística que se aplica al cargar los chats inicialmente.
        let formattedMessage = messageContent;
        if (messageContent && typeof messageContent === 'string' && messageContent.includes('.')) {
          const fileObj = getFileType(messageContent.split('.').at(-1));
          if (fileObj.isImage) formattedMessage = `<${fileObj.fileIcon}> Imagen`;
          else if (fileObj.isAudio) formattedMessage = `<${fileObj.fileIcon}> Audio`;
          else if (fileObj.isVideo) formattedMessage = `<${fileObj.fileIcon}> Video`;
          else if (fileObj.isOther && !fileObj.isUnknown) formattedMessage = `<${fileObj.fileIcon}> Archivo`;
        }
        chat.last_message = formattedMessage;
        chat.last_message_direction = direction;
        chat.isIn = direction === 'in' ? true : false;
        chat.date_message = messageDate;

        if (direction === 'out' && currentChat) {
          chat.unread = 0;
        }
        if (direction === 'in') {
          if (chat.unread) {
            chat.unread += 1;
          } else {
            chat.unread = 1;
          }
        }
        const newChats = state.chats.filter((x) => x.id != chatId);
        newChats.unshift(chat);
        // renderChats = newChats;
        // if (direction === 'in' && isFiltered ) {
        if ((direction === 'out' && currentChat) || !isFiltered) {
          // const newChats = renderChats.filter((x) => x.id != chatId);
          // newChats.unshift(chat);
          // renderChats = newChats;
          state.chats = newChats;
          state.stringJoinfilter = '';
          state.filteredChats = false;
        }
      }

      //   // const newTmpChats = renderChats.filter((x) => x.id != chatId);
      //   // newTmpChats.unshift(chat);
      //   // renderChats = newChats;
      //   state.chats = newChats;
      //   state.stringJoinfilter = '';
      // }

      // const newChats = state.chats.filter((x) => x.id !== chatId);
      // newChats.unshift(chat);
      // state.chats = newChats;

      // state.chats.splice(chatIndex, 1, chat);
    },
    UPDATE_DRAFT(state, { chatId, draft, type = 'pbx' }) {
      if (type === 'pbx') {
        let chatIndex = state.sipmovilChats.findIndex((x) => x.chatId == chatId, -1);
        if (chatIndex !== -1) {
          let chat = state.sipmovilChats[chatIndex];
          chat.draft = draft;
        }
      }
      if (type === 'join') {
        let chatIndex = state.chats.findIndex((x) => x.id == chatId, -1);
        if (chatIndex !== -1) {
          let chat = state.chats[chatIndex];
          chat.draft = draft;
        }
      }
    },
    UPDATE_SIPMOVIL_LAST_MESSAGE(
      state,
      { chatId, messageContent, direction, messageDate, message }
    ) {
      var chatIndex = state.sipmovilChats.findIndex((x) => x.chatId == chatId, -1);
      var chat = state.sipmovilChats[chatIndex];
      if (state.filteredSipmovilChats) {
        chatIndex = state.renderSipmovilChats.findIndex((x) => x.chatId == chatId, -1);
        chat = state.renderSipmovilChats[chatIndex];
      }
      if (!chat || chatIndex === -1) {
        console.warn('Chat no encontrado en UPDATE_SIPMOVIL_LAST_MESSAGE:', chatId);
        return;
      }
      if (message.kind !== 'NO_ATTACHED') {
        const fileObj = getFileType(messageContent.split('.').at(-1));
        if (fileObj.isImage && isStickerFileName(messageContent)) chat.last_message = STICKER_PREVIEW;
        else if (fileObj.isImage) chat.last_message = `<${fileObj.fileIcon}> Imagen`;
        else if (fileObj.isAudio) chat.last_message = `<${fileObj.fileIcon}> Audio`;
        else if (fileObj.isVideo) chat.last_message = `<${fileObj.fileIcon}> Video`;
        else if (fileObj.isOther) chat.last_message = `<${fileObj.fileIcon}> Archivo`;
      } else {
        chat.last_message = messageContent;
      }
      chat.last_message_incoming = direction === 'in' ? true : false;
      chat.last_message_status = message.readedMsj
        ? 'read'
        : (message.delivered ? 'delivered' : 'unread');
      chat.date_message = messageDate;
      chat.nameLastMessage = message.author[1].split(' ')[0];
      chat.idLastMessage = message.id;
      chat.deletedLastMessage = false;
      if (direction === 'out') {
        chat.unread = 0;
      }
      if (direction === 'in') {
        if (chat.unread) {
          chat.unread += 1;
        } else {
          chat.unread = 1;
        }
      }
      if (
        chat.chats.length > 0 &&
        chat.chats[chat.chats.length - 1].time === 'Hoy' &&
        !chat.chats[chat.chats.length - 1].data.includes(message)
      ) {
        chat.chats[chat.chats.length - 1].data.push(message);
      } else if (
        chat.chats.length > 0 &&
        !chat.chats[chat.chats.length - 1].data.includes(message)
      ) {
        chat.chats.push({
          time: 'Hoy',
          data: [message],
        });
      } else if (chat.chats.length === 0) {
        chat.chats.push({
          time: 'Hoy',
          data: [message],
        });
      }
      if (state.filteredSipmovilChats && direction === 'in') {
        const newChats = state.renderSipmovilChats.filter((x) => x.chatId != chatId);
        newChats.unshift(chat);
        state.renderSipmovilChats = newChats;
      } else if (state.filteredSipmovilChats && direction === 'out') {
        const newChats = state.renderSipmovilChats.filter((x) => x.chatId != chatId);
        newChats.unshift(chat);
        state.renderSipmovilChats = newChats;
        state.sipmovilChats = newChats;
        // Limpiar búsqueda cuando se envía un mensaje (IMPORTANTE: preserveSearchOnNavigation primero)
        state.preserveSearchOnNavigation = false;
        state.filteredSipmovilChats = false;
        state.foundChats = [];
        state.stringfilter = '';
      } else {
        const newTmpChats = state.sipmovilChats.filter((x) => x.chatId != chatId);
        newTmpChats.unshift(chat);
        state.sipmovilChats = newTmpChats;
        state.renderSipmovilChats = newTmpChats;
        if (!state.preserveSearchOnNavigation) {
          state.stringfilter = '';
          state.filteredSipmovilChats = false;
        }
      }
    },
    // FEX-1124: el destinatario recibió el último mensaje → ✓✓ negro en el listado.
    // Solo se marca si el mensaje entregado es el ÚLTIMO del chat (idLastMessage) y el
    // estado no es ya 'read'. Actualiza ambas listas (normal y filtrada por búsqueda).
    SET_LAST_MESSAGE_DELIVERED(state, { ids }) {
      const idSet = ids.map(String);
      const apply = (list) => {
        for (const chat of list) {
          if (chat && idSet.includes(String(chat.idLastMessage)) && chat.last_message_status !== 'read') {
            chat.last_message_status = 'delivered';
          }
        }
      };
      apply(state.sipmovilChats);
      apply(state.renderSipmovilChats);
    },
    UPDATE_SIPMOVIL_STATUS(state, { data, status }) {
      const chatIndex = state.sipmovilChats.findIndex((x) => x.account == data.endpoint, -1);
      if (chatIndex !== -1) {
        const chat = state.sipmovilChats[chatIndex];
        chat.state = status;
      }
    },
    CREATE_CHAT(state, chat) {
      chat = processSenders([chat]);
      state.chats.unshift(chat[0]);
    },
    CREATE_SIPMOVIL_CHAT(state, chat) {
      state.sipmovilChats.unshift(chat);
    },
    CREATE_RENDER_SIPMOVIL_CHAT(state, chat) {
      state.renderSipmovilChats.unshift(chat);
    },
    REMOVE_CHATS(state, chatId) {
      state.chats = state.chats.filter((x) => x.id !== chatId);
      // si estaba marcada en la transferencia selectiva, deja de contar y de enviarse
      state.selectedTransferChatIds = state.selectedTransferChatIds.filter((id) => id !== chatId);
    },
    REMOVE_SIPMOVIL_CHAT(state, chatId) {
      state.sipmovilChats = state.sipmovilChats.filter((x) => x.id !== chatId);
    },
    UPDATE_SIPMOVIL_CHAT_ACTIVE_MEMBER(state, { chatId, isActiveMember, exitReason }) {
      const updateIn = (list) => {
        const chat = list.find(c => c.chatId == chatId);
        if (!chat) return;
        chat.isActiveMember = isActiveMember;
        if (exitReason !== undefined) chat.exitReason = exitReason;
      };
      updateIn(state.sipmovilChats);
      updateIn(state.renderSipmovilChats);
    },
    // Un admin habilitó/inhabilitó al usuario (evento de socket
    // `user_state_changed`, no confundir con isActiveMember de grupos).
    // Se identifica por `id` (el id del sender/usuario que trae /api/v1/chats/,
    // el mismo con el que llega `isActive` — distinto de `chatId`, la conversación).
    UPDATE_SIPMOVIL_ACTIVE(state, { id, isActive }) {
      const updateIn = (list) => {
        const chat = list.find(c => c.id == id);
        if (!chat) return;
        chat.isActive = isActive;
      };
      updateIn(state.sipmovilChats);
      updateIn(state.renderSipmovilChats);
      updateIn(state.sipmovilChatsForward);
    },
    CLOSE_OPENED_CHATS(state) {
      state.openedChat = {};
    },
    UPDATE_ORIGIN_CHAT(state, value) {
      state.originOpenedChat = value;
    },
    RESTART_CHAT_VARS(state) {
      state.lastSipmovilMessages = false;
      state.totalSipmovilMessages = 0;
    },
    UPDATE_SIPMOVIL_CHAT_ID(state, id) {
      state.openedChat.chatId = id;
    },
    UPDATE_CHAT_CONTACTS(state, data) {
      state.openedChat.contacts = data.contacts;
    },
    UPDATE_OWNER_CHAT(state, data) {
      const chatIndex = state.chats.findIndex((x) => x.id === data.id, -1);
      if (chatIndex === -1) return;
      const chat = state.chats[chatIndex];
      const join = encryptStorage.getItem('user').join;
      chat.owner = data.owner != 'undefined' ? data.owner : join.adminUser;
    },
    UPDATE_OWNER_CHATS(state, { currentOwner, newOwner }) {
      state.chats.forEach((chat) => {
        if (chat.owner === currentOwner) {
          chat.owner = newOwner;
        }
      });
    },
    UPDATE_UNREAD_SIPMOVIL(state, value) {
      state.totalUnreadSipmovilChats = value;
      state.totalUnreadChats = state.totalUnreadSipmovilChats + state.totalUnreadJoinChats;
    },
    UPDATE_UNREAD_JOIN(state, value) {
      state.totalUnreadJoinChats = value;
      state.totalUnreadChats = state.totalUnreadSipmovilChats + state.totalUnreadJoinChats;
    },
    UPDATE_SIPMOVIL_WRITING(state, data) {
      const chatIndex = state.sipmovilChats.findIndex((x) => x.chatId == data.chat_id, -1);
      var chat = state.sipmovilChats[chatIndex];
      if (chat !== undefined) chat.writing = data.writing;
    },
    // FEX-1156: indicador "grabando audio" (espejo de UPDATE_SIPMOVIL_WRITING).
    UPDATE_SIPMOVIL_RECORDING(state, data) {
      const chatIndex = state.sipmovilChats.findIndex((x) => x.chatId == data.chat_id, -1);
      var chat = state.sipmovilChats[chatIndex];
      if (chat !== undefined) chat.recording = data.recording;
    },
    UPDATE_JOIN_WRITING(state, data) {
      const chatIndex = state.chats.findIndex((x) => x.id == data.chat_id, -1);
      var chat = state.chats[chatIndex];
      if (chat !== undefined) {
        chat.writing = data.writing;
        chat.writingAgent = chat.writing === true ? data.username : '';
      }
    },
    UPDATE_FOUNDED_CHATS(state, data) {
      state.foundJoinChats = data;
    },
    UPDATE_SIPMOVIL_FOUNDED_CHATS(state, data) {
      if (state.preserveSearchOnNavigation && Array.isArray(data) && data.length === 0 && state.foundChats.length > 0) {
        return;
      }
      state.foundChats = data;
    },
    APPEND_SIPMOVIL_FOUNDED_CHATS(state, data) {
      state.foundChats.push(...data);
    },
    // Mutaciones para búsqueda en chat
    SET_SEARCH_IN_CHAT_ACTIVE(state, value) {
      state.searchInChatActive = value;
    },
    SET_SEARCH_IN_CHAT_QUERY(state, query) {
      state.searchInChatQuery = query;
    },
    SET_SEARCH_IN_CHAT_RESULTS(state, results) {
      state.searchInChatResults = results;
      state.searchInChatCurrentIndex = 0;
    },
    SET_SEARCH_IN_CHAT_CURRENT_INDEX(state, index) {
      state.searchInChatCurrentIndex = index;
    },
    CLEAR_SEARCH_IN_CHAT(state) {
      state.searchInChatActive = false;
      state.searchInChatQuery = '';
      state.searchInChatResults = [];
      state.searchInChatCurrentIndex = 0;
    },
    SET_PRESERVE_SEARCH_ON_NAVIGATION(state, value) {
      state.preserveSearchOnNavigation = value;
    },
    SET_STATE_PAGE(state, value) {
      state.page = value;
    },
    SET_LABEL_PAGE(state, value) {
      state.labelPage = value;
    },
    SET_AGENT_PAGE(state, value) {
      state.agentPage = value;
    },
    SET_NO_REPLY_PAGE(state, value) {
      state.noReplyPage = value;
    },
    SET_UNREAD_PAGE(state, value) {
      state.unreadPage = value;
    },
    SET_BLOCK_SCROLL(state, value) {
      state.blockScroll = value;
    },
    TOGGLE_ORDER_BY_STATE(state) {
      state.orderByState = !state.orderByState;
    },
    TOGGLE_ORDER_BUTTON(state, value) {
      state.orderButton = value;
    },
    TOGGLE_ARCHIVED(state, value) {
      state.archived = value;
    },
    TOGGLE_BANNED(state, value) {
      state.banned = value;
    },
    LOAD_CLOSING_TYPES(state, data) {
      state.closingTypes = data;
    },
    TOGGLE_BULK_SELECTION_MODE(state, value) {
      state.bulkSelectionMode = value;
      if (!value) {
        state.selectedChatIds = [];
        state.bulkSelectAll = false;
        state.bulkDateFrom = null;
        state.bulkDateTo = null;
        state.bulkDateCriteria = null;
      }
    },
    TOGGLE_SELECTED_CHAT_ID(state, chatId) {
      const index = state.selectedChatIds.indexOf(chatId);
      if (index === -1) {
        state.selectedChatIds.push(chatId);
      } else {
        state.selectedChatIds.splice(index, 1);
      }
    },
    SET_BULK_SELECT_ALL(state, value) {
      state.bulkSelectAll = value;
      if (value) {
        state.selectedChatIds = [];
      }
    },
    SET_BULK_DATE_FROM(state, value) {
      state.bulkDateFrom = value;
    },
    SET_BULK_DATE_TO(state, value) {
      state.bulkDateTo = value;
    },
    SET_BULK_DATE_CRITERIA(state, value) {
      state.bulkDateCriteria = value;
    },
    SET_BULK_DOWNLOADS(state, data) {
      state.bulkDownloads = data;
    },
    TOGGLE_TRANSFER_SELECTION_MODE(state, value) {
      state.transferSelectionMode = value;
      if (!value) {
        state.selectedTransferChatIds = [];
        state.transferDateFrom = null;
        state.transferDateTo = null;
      }
    },
    TOGGLE_TRANSFER_CHAT_ID(state, chatId) {
      const index = state.selectedTransferChatIds.indexOf(chatId);
      if (index === -1) {
        state.selectedTransferChatIds.push(chatId);
      } else {
        state.selectedTransferChatIds.splice(index, 1);
      }
    },
    SET_TRANSFER_SELECTION_BUSY(state, value) {
      state.transferSelectionBusy = value;
    },
    SET_TRANSFER_PREVIOUS_CHAT(state, chat) {
      state.transferPreviousChat = chat;
    },
    SET_TRANSFER_DATE_FROM(state, value) {
      state.transferDateFrom = value;
    },
    SET_TRANSFER_DATE_TO(state, value) {
      state.transferDateTo = value;
    },
  },
  actions: {
    
    async startTransferSelection({ state, commit, dispatch }) {
      await dispatch('exitBulkSelectionMode');
      const opened = state.openedChat && state.openedChat.id ? state.openedChat : null;
      commit('SET_TRANSFER_PREVIOUS_CHAT', opened);
      if (opened) {
        commit('CLOSE_OPENED_CHATS');
      }
      commit('TOGGLE_TRANSFER_SELECTION_MODE', true);
      const pad = (n) => String(n).padStart(2, '0');
      const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const to = new Date();
      const from = new Date(to.getFullYear(), to.getMonth() - 1, to.getDate());
      await dispatch('applyTransferDateFilter', { from: formatDate(from), to: formatDate(to) });
    },
    
    async cancelTransferSelection({ state, commit, dispatch }) {
      const previous = state.transferPreviousChat;
      const hadDateFilter = !!(state.transferDateFrom || state.transferDateTo);
      commit('SET_TRANSFER_PREVIOUS_CHAT', null);
      commit('TOGGLE_TRANSFER_SELECTION_MODE', false);
      if (hadDateFilter) {
        await dispatch('reloadFilteredJoinList');
      }
      if (previous && previous.id) {
        // el chat pudo ser reemplazado en la lista por un evento del socket
        const chat = state.chats.find((x) => x.id === previous.id) || previous;
        dispatch('selectChat', chat);
      }
    },
    // FEX-1162: transfiere las conversaciones seleccionadas al agente elegido.
    // No se actualiza el owner localmente: el handler del socket calcula los
    // contadores por agente leyendo el owner que el chat tiene en el estado
    // local (realtime.js, setAgentChats), así que adelantarlo los descuadra.
    async transferSelectedConversations({ state, commit, dispatch }, { newOwner, groupName }) {
      const ids = [...state.selectedTransferChatIds];
      if (ids.length === 0 || !newOwner || state.transferSelectionBusy) return { ok: false };
      const token = encryptStorage.getItem('user').join.tokenJoin;
      commit('SET_TRANSFER_SELECTION_BUSY', true);
      try {
        const { data } = await ChatService.transferConversations(
          { conversation_ids: ids, new_owner: newOwner, group_name: groupName },
          token
        );
        const hadDateFilter = !!(state.transferDateFrom || state.transferDateTo);
        commit('SET_TRANSFER_PREVIOUS_CHAT', null);
        commit('TOGGLE_TRANSFER_SELECTION_MODE', false);
        if (hadDateFilter) {
          await dispatch('reloadFilteredJoinList');
        }
        return { ok: true, data };
      } catch (error) {
        console.log(error);
        dispatch(
          'ui/alert/displayAlert',
          {
            message: 'Operación fallida',
            description:
              error.response?.data?.error || 'No se pudieron transferir las conversaciones',
            alertType: 'error',
          },
          { root: true }
        );
        // se conservan el modo y la selección para poder reintentar
        return { ok: false };
      } finally {
        commit('SET_TRANSFER_SELECTION_BUSY', false);
      }
    },
    // Termina en bloque las conversaciones marcadas en la transferencia
    // selectiva. El backend emite "transferredChat" (state = closed) por cada
    // una y realtime.js las saca del listado; igual se quitan aquí las
    // finalizadas por si el evento no llega.
    async finishSelectedConversations({ state, commit, dispatch }) {
      const ids = [...state.selectedTransferChatIds];
      if (ids.length === 0 || state.transferSelectionBusy) return { ok: false };
      const user = encryptStorage.getItem('user');
      commit('SET_TRANSFER_SELECTION_BUSY', true);
      try {
        const { data } = await ChatService.finishConversations(
          {
            conversation_ids: ids,
            agent_name: user.Username,
            group_name: 'administrador',
            message: '',
          },
          user.join.tokenJoin
        );
        (data?.results || [])
          .filter((item) => item.status === 'finalizada')
          .forEach((item) => commit('REMOVE_CHATS', item.conversation_id));
        const hadDateFilter = !!(state.transferDateFrom || state.transferDateTo);
        commit('SET_TRANSFER_PREVIOUS_CHAT', null);
        commit('TOGGLE_TRANSFER_SELECTION_MODE', false);
        if (hadDateFilter) {
          await dispatch('reloadFilteredJoinList');
        }
        return { ok: true, data };
      } catch (error) {
        console.log(error);
        dispatch(
          'ui/alert/displayAlert',
          {
            message: 'Operación fallida',
            description:
              error.response?.data?.error || 'No se pudieron terminar las conversaciones',
            alertType: 'error',
          },
          { root: true }
        );
        // se conservan el modo y la selección para poder reintentar
        return { ok: false };
      } finally {
        commit('SET_TRANSFER_SELECTION_BUSY', false);
      }
    },
    // FEX-1188: entra al modo descarga masiva con un rango de fechas y un
    // criterio por defecto (último mes / último mensaje) para que la tabla
    // no arranque sin ningún filtro de fecha aplicado.
    async enterBulkSelectionMode({ commit, dispatch }) {
      commit('TOGGLE_BULK_SELECTION_MODE', true);
      commit('SET_BULK_DATE_CRITERIA', 'ultimo_mensaje');
      const pad = (n) => String(n).padStart(2, '0');
      const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const to = new Date();
      const from = new Date(to.getFullYear(), to.getMonth() - 1, to.getDate());
      await dispatch('applyBulkDateFilter', { from: formatDate(from), to: formatDate(to) });
    },
    // FEX-1188/FEX-1276: resetea paginación y recarga el listado con los
    // filtros de fecha vigentes (descarga masiva o transferencia selectiva).
    // Mismo patrón de reset que toggleArchived/filterUnreadNow
    // (CustomerHeaderOptions.vue).
    async reloadFilteredJoinList({ commit, dispatch }) {
      commit('SET_STATE_PAGE', 1);
      commit('conversation/RESTART_AGENTS_PAGES', null, { root: true });
      commit('SET_CHATS', []);
      dispatch('ui/toggleJoinLoading', true, { root: true });
      await dispatch('filteredSenders');
      dispatch('ui/toggleJoinLoading', false, { root: true });
    },
    // FEX-1188: aplica (o limpia, si from/to vienen null) el rango de fechas
    // de descarga masiva y recarga el listado.
    async applyBulkDateFilter({ commit, dispatch }, { from, to }) {
      commit('SET_BULK_DATE_FROM', from);
      commit('SET_BULK_DATE_TO', to);
      await dispatch('reloadFilteredJoinList');
    },
    // FEX-1188 (complementario, dropdown "Filtrar por"): cambia el criterio
    // de fecha (ninguno/primer_mensaje/ultimo_mensaje) conservando el rango
    // vigente. Solo recarga si hay fechas diligenciadas (CA-03/CA-04): sin
    // fechas, el listado debe quedar sin alteraciones.
    async changeBulkDateCriteria({ state, commit, dispatch }, criteria) {
      commit('SET_BULK_DATE_CRITERIA', criteria);
      if (state.bulkDateFrom || state.bulkDateTo) {
        await dispatch('reloadFilteredJoinList');
      }
    },
    
    async exitBulkSelectionMode({ state, commit, dispatch }) {
      const hadDateFilter = !!(state.bulkDateFrom || state.bulkDateTo);
      commit('TOGGLE_BULK_SELECTION_MODE', false);
      if (hadDateFilter) {
        await dispatch('reloadFilteredJoinList');
      }
    },
    
    async applyTransferDateFilter({ commit, dispatch }, { from, to }) {
      commit('SET_TRANSFER_DATE_FROM', from);
      commit('SET_TRANSFER_DATE_TO', to);
      await dispatch('reloadFilteredJoinList');
    },
    async startBulkDownload({ state, dispatch }) {
      const payload = {
        state_filter: 'todo', // el backend lo ignora cuando hay conversation_ids o select_all
        select_all: state.bulkSelectAll,
        conversation_ids: state.bulkSelectAll ? [] : state.selectedChatIds,
      };
      if (state.bulkDateFrom || state.bulkDateTo) {
        payload.date_criteria = state.bulkDateCriteria || 'ninguno';
        if (state.bulkDateFrom) payload.date_from = state.bulkDateFrom;
        if (state.bulkDateTo) payload.date_to = state.bulkDateTo;
      }
      try {
        const { data } = await ChatService.bulkDownloadConversations(payload);
        dispatch('ui/alert/displayAlert', {
          description: 'Descarga masiva',
          message: data.message,
          alertType: 'success',
        }, { root: true });
        await dispatch('exitBulkSelectionMode');
        return true;
      } catch (error) {
        const status = error?.response?.status;
        const message = error?.response?.data?.error || 'No se pudo iniciar la descarga masiva, intente más tarde.';
        dispatch('ui/alert/displayAlert', {
          description: 'Descarga masiva',
          message,
          alertType: status === 409 ? 'warning' : 'error',
        }, { root: true });
        return false;
      }
    },
    async fetchBulkDownloadStatus({ commit }) {
      const { data } = await ChatService.getBulkDownloadStatus();
      commit('SET_BULK_DOWNLOADS', data.data);
    },
    async fetchChats({ state, commit }) {
      const user = encryptStorage.getItem('user').Username;
      return ChatService.getChats(user, state.page) // user
        .then((response) => {
          console.log('Retiving dialogs in page ', state.page, response);
          commit(
            'SET_CHATS_TOTAL',
            parseInt(response.data.count, 10)
          );
          commit('LOAD_CHATS', response.data.results);
          if (response.data.next !== null) {
            state.page += 1;
          } else {
            state.page = 0;
            commit('SET_BLOCK_SCROLL', true);
          }
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async fetchJoinChatsByMessages({ state, commit }, characters) {
      const closedChats = state.archived;
      const isAdmin = encryptStorage.getItem('user').join.isAdmin;
      const username = encryptStorage.getItem('user').Username;
      const agent = isAdmin !== true ? username : null;
      return ChatService.getJoinChatsByMessages(characters, username, closedChats, null, agent)
        .then((response) => {
          commit('contact/LOAD_CONTACTS', response.data.senders, { root: true });
          commit('UPDATE_FOUNDED_CHATS', response.data.matches);
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async getJoinFilteredMessages({ state, commit, dispatch, rootState }, chatData) {
      commit('CLOSE_OPENED_CHATS');
      
      const targetChatId = chatData['chatId'];
      const messageId = chatData['message'] ? chatData['message'][0] : null;
      
      //console.log('Buscando chat con ID:', targetChatId);
      if (messageId) {
        console.log('Con mensaje específico ID:', messageId);
      }
      //console.log('state.chats tiene:', state.chats.length, 'chats');

      let chat = state.chats.find((x) => x.id === targetChatId);
      
      if (chat) {
        //console.log('Chat encontrado en state.chats');
        await dispatch('selectChat', chat);

        if (messageId) {
          //console.log('Cargando mensajes desde el mensaje', messageId, 'con API de Join');
          try {
            const response = await ChatService.getJoinFilteredMessages(chatData);
            //console.log('Mensajes obtenidos:', response.data);
            
            if (response.data.messages && response.data.messages.length > 0) {
              commit('conversation/LOAD_MESSAGES', processMessages(response.data.messages), { root: true });
              
              if (response.data.total >= 20) {
                const calculated_page = response.data.total / 20 + 1;
                commit('conversation/UPDATE_PAGE', calculated_page, { root: true });
              }
              
              commit('conversation/SET_STATUS', false, { root: true });

              setTimeout(() => {
                //console.log('Haciendo scroll al mensaje:', messageId);

                const messageElement = document.getElementById(messageId.toString());
                if (messageElement) {
                  //console.log('Elemento encontrado, haciendo scroll...');
                  messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  messageElement.classList.add('highlight-message');
                  setTimeout(() => {
                    messageElement.classList.remove('highlight-message');
                  }, 3000);
                } else {
                  console.warn('Elemento del mensaje no encontrado en el DOM');
                  console.warn('MessageId buscado:', messageId);
                }
              }, 500);
            }
          } catch (error) {
            console.error('Error al cargar mensajes:', error);
          }
        }
        
        return;
      }
      
      //console.log('renderChats tiene:', rootState.ui.customerOptions.renderChats.length, 'chats');
      if (rootState.ui.customerOptions.renderChats && rootState.ui.customerOptions.renderChats.length > 0) {
        chat = rootState.ui.customerOptions.renderChats.find((x) => x.id === targetChatId);
        
        if (chat) {
          //console.log('Chat encontrado en renderChats (chats originales)');
          commit('CREATE_CHAT', chat);
          await dispatch('selectChat', chat);

          if (messageId) {
            //console.log('Cargando mensajes desde el mensaje', messageId, 'con API de Join');
            try {
              const response = await ChatService.getJoinFilteredMessages(chatData);
              //console.log('Mensajes obtenidos:', response.data);
              
              if (response.data.messages && response.data.messages.length > 0) {
                commit('conversation/LOAD_MESSAGES', processMessages(response.data.messages), { root: true });
                
                if (response.data.total >= 20) {
                  const calculated_page = response.data.total / 20 + 1;
                  commit('conversation/UPDATE_PAGE', calculated_page, { root: true });
                }
                
                commit('conversation/SET_STATUS', false, { root: true });

                setTimeout(() => {
                  //console.log('Haciendo scroll al mensaje:', messageId);
                  const messageElement = document.getElementById(messageId.toString());
                  if (messageElement) {
                    //console.log('Elemento encontrado, haciendo scroll...');
                    messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    messageElement.classList.add('highlight-message');
                    setTimeout(() => {
                      messageElement.classList.remove('highlight-message');
                    }, 3000);
                  } else {
                    console.warn('Elemento del mensaje no encontrado en el DOM');
                    console.warn('MessageId buscado:', messageId);
                  }
                }, 500);
              }
            } catch (error) {
              console.error('Error al cargar mensajes:', error);
            }
          }
          
          return;
        }
      }
      
      console.error('❌ Chat no encontrado ni en state.chats ni en renderChats');
      console.error('💡 El chat', targetChatId, 'no está cargado en memoria.');
      console.error('💡 Necesitas cargar más chats o el chat está en otra página.');
    },
    async fetchChatsByMessages({ commit, dispatch }, characters) {
      return ChatService.getChatsByMessages(characters)
        .then((response) => {
          commit('UPDATE_SIPMOVIL_FOUNDED_CHATS', response.data.matches);
          dispatch('ui/toggleJoinLoading', false, { root: true });
          commit('SET_ALL_MESSAGES_IN_VIEW', false);
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async fetchAllChatsByMessages({ state, commit, dispatch }) {
      commit('SET_ALL_MESSAGES_IN_VIEW', true);
      return ChatService.getAllChatsByMessages(state.stringfilter)
        .then((response) => {
          commit('APPEND_SIPMOVIL_FOUNDED_CHATS', response.data.matches);
          dispatch('ui/toggleJoinLoading', false, { root: true });
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async searchMessagesInChat({ commit }, { chatId, query }) {
      if (!query || query.trim() === '') {
        commit('CLEAR_SEARCH_IN_CHAT');
        return;
      }

      try {
        commit('SET_SEARCH_IN_CHAT_QUERY', query);
        const response = await ChatService.getChatsByMessages(query, chatId);

        if (response.data.matches && response.data.matches.length > 0) {
          const transformedMatches = response.data.matches.map(match => ({
            messageId: match.message[0],
            text: match.message[1],
            caption: match.caption || '',
            timestamp: match.message[2],
            author: match.author,
            chatId: match.chatId,
            isGroup: match.isGroup,
          }));

          commit('SET_SEARCH_IN_CHAT_RESULTS', transformedMatches);
          commit('SET_SEARCH_IN_CHAT_ACTIVE', true);
          return transformedMatches;
        } else {
          commit('SET_SEARCH_IN_CHAT_RESULTS', []);
          commit('SET_SEARCH_IN_CHAT_ACTIVE', true);
          return [];
        }
      } catch (error) {
        console.error('Error al buscar en el chat:', error);
        commit('CLEAR_SEARCH_IN_CHAT');
        return [];
      }
    },
    async searchMessagesInJoinChat({ commit }, { chatId, query }) {
      if (!query || query.trim() === '') {
        commit('CLEAR_SEARCH_IN_CHAT');
        return;
      }

      try {
        commit('SET_SEARCH_IN_CHAT_QUERY', query);
        const isAdmin = encryptStorage.getItem('user').join.isAdmin;
        const username = encryptStorage.getItem('user').Username;
        const agent = isAdmin !== true ? username : null;

        const response = await ChatService.getJoinChatsByMessages(query, username, false, chatId, agent);

        if (response.data.matches && response.data.matches.length > 0) {
          const transformedMatches = response.data.matches.map(match => ({
            messageId: match.message[0],
            text: match.message[1],
            caption: match.caption || '',
            timestamp: match.message[2],
            author: match.author,
            chatId: match.chatId,
            isGroup: match.isGroup,
          }));

          commit('SET_SEARCH_IN_CHAT_RESULTS', transformedMatches);
          commit('SET_SEARCH_IN_CHAT_ACTIVE', true);
          return transformedMatches;
        } else {
          commit('SET_SEARCH_IN_CHAT_RESULTS', []);
          commit('SET_SEARCH_IN_CHAT_ACTIVE', true);
          return [];
        }
      } catch (error) {
        console.error('Error al buscar en el chat de Join:', error);
        commit('CLEAR_SEARCH_IN_CHAT');
        return [];
      }
    },
    scrollToSearchResult({ state, commit }, direction) {
      const results = state.searchInChatResults;
      if (results.length === 0) return;
      
      let newIndex = state.searchInChatCurrentIndex;
      
      if (direction === 'next') {
        newIndex = (newIndex + 1) % results.length;
      } else if (direction === 'prev') {
        newIndex = newIndex - 1;
        if (newIndex < 0) newIndex = results.length - 1;
      }
      
      commit('SET_SEARCH_IN_CHAT_CURRENT_INDEX', newIndex);
      
      const currentResult = results[newIndex];
      const messageId = currentResult.messageId;
      
      setTimeout(() => {
        const messageElement = document.getElementById(messageId.toString());
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Remover highlight anterior
          document.querySelectorAll('.highlight-search').forEach(el => {
            el.classList.remove('highlight-search');
          });
          // Agregar nuevo highlight
          messageElement.classList.add('highlight-search');
        }
      }, 100);
    },
    async getSipmovilFilteredMessages({ state, commit, dispatch }, chatData) {
      const targetChatId = chatData['chatId'];
      const messageId = chatData['messageId'];
      
      let chat = state.sipmovilChats.find((x) => x.chatId == targetChatId);
      if (chat) {
        await dispatch('selectSipmovilChat', chat);
        
        if (messageId) {
          try {
            const response = await ChatService.getSipmovilFilteredMessages({ chatId: targetChatId, messageId });
            
            if (response.data.messages && response.data.messages.length > 0) {
              commit('conversation/LOAD_MESSAGES', processSipmovilMessages(response.data.messages), { root: true });
              commit('conversation/SET_STATUS', false, { root: true });
              
              setTimeout(() => {
                const messageElement = document.getElementById(messageId.toString());
                if (messageElement) {
                  messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  document.querySelectorAll('.highlight-search').forEach(el => {
                    el.classList.remove('highlight-search');
                  });
                  messageElement.classList.add('highlight-search');
                } else {
                  console.warn('Elemento del mensaje no encontrado en el DOM');
                }
              }, 500);
            }
          } catch (error) {
            console.error('Error al cargar mensajes de sipmovil:', error);
          }
        }
      } else {
        console.error('Chat no encontrado en sipmovilChats');
      }
    },
    async fetchChatsbyAgents({ state, commit, rootState }, newPage = false) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      const noReply = rootState.auth.onlyNoReply;
      commit('SET_BLOCK_SCROLL', true);
      return ChatService.getChatsbyAgents(
        state.stringAgents,
        token,
        state.agentPage,
        `&noReply=${noReply}&unread=${state.filterUnread}&labels_id=${state.stringLabels}`
      ) // user
        .then((response) => {
          commit('SET_BLOCK_SCROLL', false);
          commit(
            'SET_CHATS_TOTAL',
            parseInt(response.data.count, 10) // response.headers['x-total-count']
          );
          if (newPage) {
            commit('LOAD_CHATS', processSenders(response.data.results));
          } else {
            commit('SET_CHATS', processSenders(response.data.results));
          }
          if (response.data.next !== null) {
            state.agentPage += 1;
          } else {
            state.agentPage = 0;
            commit('SET_BLOCK_SCROLL', true);
          }
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async fetchChatsbyLabels({ state, commit, rootState }, newPage = false) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      const isAdmin = join.isAdmin;
      const owner = encryptStorage.getItem('user').Username;
      const agroupedConv = rootState.auth_store.userInfo.join.agrupedConversations;
      const noReply = rootState.auth.onlyNoReply;
      commit('SET_BLOCK_SCROLL', true);
      return ChatService.getChatsbyLabels(
        state.stringLabels,
        token,
        owner,
        isAdmin,
        state.labelPage,
        `&noReply=${noReply}&unread=${state.filterUnread}&agroupedConv=${agroupedConv}&agent=${state.stringAgents}`
      ) // user
        .then((response) => {
          commit('SET_BLOCK_SCROLL', false);
          commit(
            'SET_CHATS_TOTAL',
            parseInt(response.data.count, 10) // response.headers['x-total-count']
          );
          if (newPage) {
            commit('LOAD_CHATS', processSenders(response.data.results));
          } else {
            commit('SET_CHATS', processSenders(response.data.results));
          }
          if (response.data.next !== null) {
            state.labelPage += 1;
          } else {
            state.labelPage = 0;
            commit('SET_BLOCK_SCROLL', true);
          }
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async fetchChatsbyNoReply({ state, commit, dispatch, rootState }, newPage = false) {
      const join = encryptStorage.getItem('user').join;
      const owner = encryptStorage.getItem('user').Username;
      const token = join.tokenJoin;
      const isAdmin = join.isAdmin;
      const agroupedConv = rootState.auth_store.userInfo.join.agrupedConversations;
      commit('SET_BLOCK_SCROLL', true);
      if (!agroupedConv) {
        return ChatService.getChatsbyNoReply(
          token,
          owner,
          isAdmin,
          state.noReplyPage,
          `&unread=${state.filterUnread}&agroupedConv=${agroupedConv}&agent=${state.stringAgents}&labels_id=${state.stringLabels}`
        )
          .then((response) => {
            commit('SET_BLOCK_SCROLL', false);
            commit(
              'SET_CHATS_TOTAL',
              parseInt(response.data.count, 10) // response.headers['x-total-count']
            );
            const data = agroupedConv ? response.data : response.data.results;
            if (newPage && !agroupedConv) {
              commit('LOAD_CHATS', processSenders(data));
            } else {
              commit('SET_CHATS', processSenders(data));
            }
            if (response.data.next !== null && !agroupedConv) {
              state.noReplyPage += 1;
            } else {
              state.noReplyPage = 0;
              commit('SET_BLOCK_SCROLL', true);
            }
            dispatch('ui/toggleJoinLoading', false, { root: true });
            return 'Success';
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        commit('SET_BLOCK_SCROLL', true);
        for (const agent of rootState.conversation.transferAgents) {
          if (agent.page > 0) {
            ChatService.getChatsbyAgents(
              agent.userName,
              token,
              agent.page,
              `&noReply=true&unread=${state.filterUnread}&agent=${state.stringAgents}&labels_id=${state.stringLabels}`
            ) // user
              .then((response) => {
                commit('SET_BLOCK_SCROLL', false);
                commit(
                  'SET_CHATS_TOTAL',
                  parseInt(response.data.count, 10) // response.headers['x-total-count']
                );
                const dialogs = processSenders(response.data.results);
                commit('LOAD_CHATS', dialogs);
                dispatch('ui/toggleJoinLoading', false, { root: true });
                if (response.data.next !== null) {
                  agent.page += 1;
                } else {
                  agent.page = 0;
                }
              })
              .catch((error) => {
                console.log(error);
              });
          }
        }
      }
    },
    async fetchChatsbyUnread({ state, commit, dispatch, rootState }, newPage = false) {
      const join = encryptStorage.getItem('user').join;
      const owner = encryptStorage.getItem('user').Username;
      const token = join.tokenJoin;
      const isAdmin = join.isAdmin;
      const agroupedConv = rootState.auth_store.userInfo.join.agrupedConversations;
      const noReply = rootState.auth.onlyNoReply;
      commit('SET_BLOCK_SCROLL', true);
      if (!agroupedConv) {
        return ChatService.getChatsbyUnread(
          token,
          owner,
          isAdmin,
          state.unreadPage,
          `&noReply=${noReply}&agroupedConv=${agroupedConv}&agent=${state.stringAgents}&labels_id=${state.stringLabels}`
        )
          .then((response) => {
            commit('SET_BLOCK_SCROLL', false);
            commit(
              'SET_CHATS_TOTAL',
              parseInt(response.data.count, 10) // response.headers['x-total-count']
            );
            const data = agroupedConv ? response.data : response.data.results;
            if (newPage && !agroupedConv) {
              commit('LOAD_CHATS', processSenders(data));
            } else {
              commit('SET_CHATS', processSenders(data));
            }
            if (response.data.next !== null && !agroupedConv) {
              state.unreadPage += 1;
            } else {
              state.unreadPage = 0;
              commit('SET_BLOCK_SCROLL', true);
            }
            dispatch('ui/toggleJoinLoading', false, { root: true });
            return 'Success';
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        commit('SET_BLOCK_SCROLL', true);
        for (const agent of rootState.conversation.transferAgents) {
          if (agent.page > 0) {
            ChatService.getChatsbyAgents(
              agent.userName,
              token,
              agent.page,
              `&unread=true&noReply=${noReply}&agroupedConv=${agroupedConv}&agent=${state.stringAgents}&labels_id=${state.stringLabels}`
            ) // user
              .then((response) => {
                commit('SET_BLOCK_SCROLL', false);
                commit(
                  'SET_CHATS_TOTAL',
                  parseInt(response.data.count, 10) // response.headers['x-total-count']
                );
                const dialogs = processSenders(response.data.results);
                commit('LOAD_CHATS', dialogs);
                dispatch('ui/toggleJoinLoading', false, { root: true });
                if (response.data.next !== null) {
                  agent.page += 1;
                } else {
                  agent.page = 0;
                }
              })
              .catch((error) => {
                console.log(error);
              });
          }
        }
      }
    },
    selectChat({ commit, dispatch, rootState }, chat) {
      // cancel() on AudioRecord
      commit('STOP_RECORDING');
      const isEmojiOpen = rootState.ui.emojis.showEmojis;
      if (isEmojiOpen === true) {
        commit('ui/emojis/TOGGLE_EMOJI', null, { root: true });
      }
      const isAttachOpen = rootState.ui.attachments.showAttachments;
      if (isAttachOpen === true) {
        commit('ui/attachments/TOGGLE_ATTACHMENT', null, { root: true });
        commit('ui/attachments/SHOW_DRAG_PREV', false, { root: true });
      }
      const isLabel = rootState.ui.labels.showLabels;
      if (isLabel === true) {
        commit('ui/labels/TOGGLE_LABELS', null, { root: true });
      }
      const directionChat = chat.last_message_direction;
      if (directionChat === 'in' && chat.unread > 0) {
        dispatch(
          'conversation/setAgentLastMessage',
          { owner: chat.owner, direction: 'in' },
          { root: true }
        );
      }
      commit('template/TOGGLE_TEMPLATES', false, { root: true });
      // Guardar draft del chat actual antes de cambiar
      const currentMessage = rootState.conversation.currentMessage;
      const oldChat = rootState.chat.openedChat;
      if (oldChat?.id && currentMessage.length > 0) {
        commit('UPDATE_DRAFT', { chatId: oldChat.id, draft: currentMessage, type: 'join' });
      }
      commit('SET_CHAT', chat);
      dispatch('updateUnread', chat.id);
      commit('conversation/UPDATE_PAGE', 1, { root: true });
      commit('conversation/CLEAN_MESSAGES', null, { root: true });
      dispatch('conversation/fetchSenderMessages', chat.id, { root: true });
      commit('conversation/UPDATE_MESSAGE', chat.draft ? chat.draft : '', { root: true });
      if (chat.draft) {
        commit('UPDATE_DRAFT', { chatId: chat.id, draft: '', type: 'join' });
      }
      ReminderService.getBySender(chat.id)
        .then((res) => {
          const reminder = res.data;
          if (reminder && reminder.id) {
            commit('SET_CHAT_REMINDER', { senderId: chat.id, reminder });
          } else {
            commit('REMOVE_CHAT_REMINDER', chat.id);
          }
        })
        .catch(() => {});
    },
    selectMessageChat({ state, commit, rootState }, chat) {
      // cancel() on AudioRecord
      commit('STOP_RECORDING');
      const isEmojiOpen = rootState.ui.emojis.showEmojis;
      if (isEmojiOpen === true) {
        commit('ui/emojis/TOGGLE_EMOJI', null, { root: true });
      }
      const isAttachOpen = rootState.ui.attachments.showAttachments;
      if (isAttachOpen === true) {
        commit('ui/attachments/TOGGLE_ATTACHMENT', null, { root: true });
        commit('ui/attachments/SHOW_DRAG_PREV', false, { root: true });
      }
      const isLabel = rootState.ui.labels.showLabels;
      if (isLabel === true) {
        commit('ui/labels/TOGGLE_LABELS', null, { root: true });
      }
      commit('template/TOGGLE_TEMPLATES', false, { root: true });
      commit('conversation/UPDATE_PAGE', 1, { root: true });
      commit('conversation/CLEAN_MESSAGES', null, { root: true });
      commit('conversation/UPDATE_MESSAGE', '', { root: true });
      state.openedChat = chat;
    },
    openChatByReminderId({ state, dispatch }, senderId) {
      const chat = state.chats.find((c) => c.id === senderId);
      if (chat) {
        dispatch('selectChat', chat);
      }
    },
    fetchRemindersForChats({ commit }, chats) {
      if (!chats || chats.length === 0) return;
      chats.forEach((chat) => {
        ReminderService.getBySender(chat.id)
          .then((res) => {
            const reminder = res.data;
            if (reminder && reminder.id) {
              commit('SET_CHAT_REMINDER', { senderId: chat.id, reminder });
            }
          })
          .catch(() => {});
      });
    },
    async selectSipmovilChat({ commit, dispatch, rootState }, chat) {
      console.log('🟣 selectSipmovilChat - Chat recibido:', {
        contact_dialog: chat.contact_dialog,
        name: chat.name,
        getInfoName: chat.getInfoName,
        chatId: chat.chatId,
        id: chat.id,
        isGroup: chat.isGroup,
        fullChat: chat
      });
      // cancel() on AudioRecord
      commit('STOP_RECORDING');
      const isEmojiOpen = rootState.ui.emojis.showEmojis;
      if (isEmojiOpen === true) {
        commit('ui/emojis/TOGGLE_EMOJI', null, { root: true });
      }
      const isAttachOpen = rootState.ui.attachments.showAttachments;
      if (isAttachOpen === true) {
        commit('ui/attachments/TOGGLE_ATTACHMENT', null, { root: true });
        commit('ui/attachments/SHOW_DRAG_PREV', false, { root: true });
      }
      commit('template/TOGGLE_TEMPLATES', false, { root: true });
      // Guardar draft del chat actual antes de cambiar
      const currentMessage = rootState.conversation.currentMessage;
      const oldChat = rootState.chat.openedChat;
      if (oldChat?.chatId && currentMessage.length > 0) {
        commit('UPDATE_DRAFT', { chatId: oldChat.chatId, draft: currentMessage });
      }
      commit('SET_SIPMOVIL_CHAT', chat);
      console.log('🟣 selectSipmovilChat - openedChat después de SET:', rootState.chat.openedChat);
      // dispatch('chat/updateUnread', chat.id, { root: true });
      if (!chat.chatId) {
        console.log('🟣 selectSipmovilChat - Chat sin chatId, creando...');
        await dispatch('createSipmovilChat', chat.id);
        console.log('🟣 selectSipmovilChat - Chat creado, chatId:', rootState.chat.openedChat.chatId);
      }
      commit(
        'ui/messagesOptions/ADD_MESSAGE_IN_PREVIEW',
        {
          value: false,
          msg: '',
        },
        { root: true }
      );
      commit('conversation/CLEAN_MESSAGES', null, { root: true });
      commit('conversation/UPDATE_MESSAGE', chat.draft ? chat.draft : '', { root: true });
      if (chat.draft) {
        commit('UPDATE_DRAFT', { chatId: chat.chatId, draft: '' });
      }
      dispatch('updateUnreadSipmovil');
      return 'Success';
    },
    async getOrCreateChat({ state, commit, dispatch }, chatName) {
      const join = encryptStorage.getItem('user').join;
      const userName = encryptStorage.getItem('user').Username;
      const token = join.tokenJoin;

      const chats = state.chats.filter(
        (x) => x.contact_dialog === chatName || x.destination === chatName
      );
      if (chats.length > 0) {
        dispatch('selectChat', chats[0]);
        ChatService.transferSender('', state.openedChat.id, userName, token, false);
        chats[0].owner = userName;
      }
      // console.log('CHATS:', chats);
      return ChatService.getOrCreateChat(userName, token, chatName) // user
        .then((response) => {
          if (response.status === 201) {
            const chat = response.data;
            // chat.img_dialog = `${serverUrl}${chat.img_dialog}`;
            commit('SET_NEW_CHAT', chat);
            dispatch('selectChat', chat);
          }
          // else if (response.status === 200) {
          // }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    // FC-550: resuelve la conversación de Join que corresponde a un teléfono,
    // SIN inferir el código de país. El CRM guarda el número sin indicativo
    // (ej. "3173667827") mientras que `destination` de Join lo trae completo
    // (ej. "573173667827"), así que se compara por los últimos dígitos y el
    // indicativo sale del registro encontrado. Sólo cuando no existe ninguna
    // conversación hay que preguntar el país al agente (lo hace el widget).
    //
    // Devuelve { status, ... }:
    //   found      → { chat, alreadySelected } conversación lista para abrir
    //   ambiguous  → { candidates }            varios contactos posibles
    //   not_found  → { national }              hay que crearla
    //   error      → { }                       no se pudo resolver (CA-04)
    async resolveJoinChatByPhone({ state, commit, dispatch }, phone) {
      const digits = String(phone || '').replace(/\D/g, '');
      if (!digits) return { status: 'error' };
      // Los últimos 10 dígitos son el número nacional en los países que opera
      // la plataforma; para números más cortos se usa lo que haya.
      const national = digits.slice(-10);

      // 1. ¿Está ya en mi bandeja? Entonces la conversación es mía y se abre
      //    sin más. Ojo: `filtered-senders/` filtra por &owner cuando no eres
      //    admin, así que state.chats SÓLO tiene las conversaciones asignadas a
      //    ti — una cerrada o de otro agente no aparece aquí aunque exista.
      const local = state.chats.filter(
        (c) => String(c.destination || '').replace(/\D/g, '').endsWith(national)
      );
      if (local.length > 0) return { status: 'found', chat: local[0] };

      // 2. Buscar el contacto en Join (`search/`). Es el buscador que usa el
      //    modal del módulo y encuentra el registro sin importar de quién sea
      //    la conversación; además devuelve el teléfono TAL COMO está guardado,
      //    que puede no coincidir con el del CRM (un fijo 6019145770 vive en
      //    Join como 19145770). Por eso se busca por el nacional y se deja que
      //    el backend haga la correspondencia.
      try {
        const username = encryptStorage.getItem('user').Username;
        const response = await ContactService.getContacts(national, username);
        const results = response.data.results || [];
        if (results.length !== 1) {
          return {
            status: results.length > 1 ? 'ambiguous' : 'not_found',
            candidates: results,
            national,
          };
        }

        // 3. Misma secuencia que CustomModal.selectContact: meter el contacto en
        //    el listado y pedir la conversación, lo que además la reasigna al
        //    agente (transferSender) — imprescindible para poder escribirle.
        const contact = results[0];
        const identifier =
          contact.phone || contact.destination || contact.custom_name || contact.name;
        // Dedup por id: el contacto puede estar ya en el listado con el teléfono
        // en otro formato, y entonces el paso 1 no lo habría reconocido.
        commit('ADD_CHAT_IF_MISSING', processSenders([contact])[0]);
        await dispatch('getOrCreateChat', String(identifier));
        const opened = state.openedChat;
        if (opened && opened.id === contact.id) {
          return { status: 'found', chat: opened, alreadySelected: true };
        }
        return { status: 'not_found', national, candidates: results };
      } catch (error) {
        console.error('[resolveJoinChatByPhone] error resolviendo el teléfono:', error);
        return { status: 'error' };
      }
    },
    getChat({ state }, id) {
      const chat = state.chats.filter((x) => x.id == id);
      console.log(chat);
      return chat[0];
    },
    async createSipmovilChat({ commit, state }, userId) {
      console.log('🟡 createSipmovilChat - userId:', userId);
      
      try {
        const response = await ChatService.createSipmovilSender([userId]);
        
        console.log('🟡 createSipmovilChat - Respuesta:', response.data);
        
        if (response.data?.chat_id) {
          // Actualizar chatId en openedChat
          commit('UPDATE_SIPMOVIL_CHAT_ID', response.data.chat_id);
          
          console.log('🟡 createSipmovilChat - chatId actualizado:', response.data.chat_id);
          console.log('🟡 createSipmovilChat - openedChat.contact_dialog:', state.openedChat.contact_dialog);
          
          return 'Success';
        } else {
          console.error('🟡 createSipmovilChat - No se recibió chat_id');
          return 'Error';
        }
      } catch (error) {
        console.error('🟡 createSipmovilChat - ERROR:', error);
        throw error;
      }
    },
    async editSipmovilChat({ state, commit }, formData) {
      return ChatService.editGroupSipmovil(formData)
        .then((response) => {
          commit('UPDATE_SIPMOVIL_CHAT_ID', response.data.chat_id);
          const user = encryptStorage.getItem('user');
          state.openedChat.contacts = response.data.participants;
          state.openedChat.contact_dialog = response.data.name;
          state.openedChat.description = response.data.description;
          state.openedChat.onlyAdminsMessages = response.data.onlyAdminsMessages;
          state.openedChat.img_dialog = `https://${user.PbxServer}${response.data.img_dialog}`;
          return 'Success';
        })

        .catch((error) => {
          console.log(error);
        });
    },
    async createGroup({ state, commit, dispatch }, { ids, name, onlyAdminsMessages }) {
      const user = encryptStorage.getItem('user');
      //console.log('createGroup - Creando grupo:', { ids, name, onlyAdminsMessages });
      
      try {
        const response = await ChatService.createSipmovilSender(ids, name, onlyAdminsMessages, true);
        const chatId = response.data.chat_id;
        const groupName = response.data.name || name;
        const chatIndex = state.sipmovilChats.findIndex(x => x.chatId == chatId);
        let newChat;
        
        if (chatIndex === -1) {
          newChat = {
            id: chatId,
            chatId: chatId,
            getInfoName: groupName,
            contact_dialog: groupName,
            last_message: '',
            date_message: '',
            img_dialog: `https://${user.PbxServer}/static/images/group.png`,
            unread: 0,
            state: 'online',
            chats: [],
            isGroup: true,
            onlyAdminsMessages: onlyAdminsMessages,
          };
          
         // console.log('createGroup - Nuevo chat creado:', newChat);
          commit('CREATE_SIPMOVIL_CHAT', newChat);
        } else {
          newChat = state.sipmovilChats[chatIndex];
          newChat.contact_dialog = groupName;
          // console.log('createGroup - Chat ya existía, actualizando:', newChat);
        }

        await dispatch('selectSipmovilChat', newChat);
        await dispatch('conversation/getChatData', chatId, { root: true });
        commit('SET_RECENT_CHAT_GROUP_CREATED_ID', chatId);
        
        // console.log('createGroup - Grupo creado exitosamente');
        return 'Success';
      } catch (error) {
        console.error('🔵 createGroup - ERROR:', error);
        throw error;
      }
    },
    async transferChat(
      { state, commit, dispatch },
      { agentName, groupName, finishConversation, message = '', alertDescription = null }
    ) {
      if (finishConversation !== true && (!agentName || typeof agentName !== 'string')) {
        dispatch(
          'ui/alert/displayAlert',
          {
            message: 'Operación fallida',
            description: 'No se pudo identificar el agente destino, intenta nuevamente.',
            alertType: 'error',
          },
          { root: true }
        );
        return { ok: false };
      }
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      return ChatService.transferSender(
        groupName,
        state.openedChat.id,
        agentName,
        token,
        finishConversation,
        message
      )
        .then((response) => {
          const isAdmin = join.isAdmin;
          // eliminar la conversacion si se transfiere exitosamente, si es agente.
          // Al finalizar, la conversación queda cerrada/archivada para TODOS los roles
          // (la lista activa la trae la API con archived=false, y ya no le pertenece),
          // así que se remueve sin importar si es admin o agente.
          if (isAdmin === false || finishConversation === true) {
            commit('REMOVE_CHATS', response.data.id);
            state.openedChat = {};
          } else {
            state.openedChat.last_message_direction = 'out';
            // si es administrador cambiar el owner del dialogo para bloquear
            // la edición por el administrados
            // commit("chat/UPDATE_OWNER_CHAT", state.openedChat, { root: true })
            // const chatIndex = state.chats.findIndex(
            //   (x) => x.id === state.openedChat.id,
            //   -1
            // );
            // state.chats.splice(chatIndex, 1, chat);
            state.openedChat = {};
          }
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Operación exitosa',
              description: alertDescription || `La conversación se transfirió al agente ${response.data.agent_name}`,
              alertType: 'success',
            },
            { root: true }
          );
          if (finishConversation !== true) {
            commit('ui/modal/TOGGLE_MODAL', null, { root: true });
          }
        })
        .catch((error) => {
          console.log(error);
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Operación fallida',
              description: 'No se pudo transferir la conversación',
              alertType: 'error',
            },
            { root: true }
          );
        });
    },
    async editFexIAChat({ state, commit }, data) {
      return ChatService.editFexIASender(data)
        .then((response) => {
          commit('SET_BLOCK_SCROLL', false);
          state.openedChat.contact_dialog = "FexIA " + response.data.name
          // dispatch('selectFexIAChat', state.openedChat);
          // const dialogs = processSipmovilSenders(response.data.value);
          // commit('LOAD_SIPMOVIL_CHATS', dialogs);
          // commit('LOAD_RENDER_SIPMOVIL_CHATS', dialogs);
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async createFexIAChat({ state, commit, dispatch }, data) {
      return ChatService.createFexIASender(data)
        .then(async (response) => {
          commit('SET_FEXIA_NEW_CHAT', {
            ...response.data,
            project_name: response.data.project_name || data.project,
          });
          await dispatch('selectFexIAChat', state.openedChat);
          commit('conversation/UPDATE_PAGE', 1, { root: true });
          commit('conversation/SET_STATUS', true, { root: true });
          dispatch('conversation/getSipmovilMessages', state.openedChat.chatId, { root: true });
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async createFexIAProject({ state, commit, dispatch }, data) {
      return ChatService.createFexIAProject(data)
        .then(async (response) => {
          commit('SET_FEXIA_NEW_CHAT', {
            ...response.data,
            project_name: response.data.project_name || data.name,
          });
          await dispatch('selectFexIAChat', state.openedChat);
          commit('conversation/UPDATE_PAGE', 1, { root: true });
          commit('conversation/SET_STATUS', true, { root: true });
          dispatch('conversation/getSipmovilMessages', state.openedChat.chatId, { root: true });
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async deleteFexIAChat({ state, commit }) {
      return ChatService.deleteFexIAChat(state.openedChat.chatId)
        .then((response) => {
          commit('REMOVE_SIPMOVIL_CHAT', response.data.chat_id);
          commit('CLOSE_OPENED_CHATS');
          // const dialogs = processSipmovilSenders(response.data.value);
          // commit('LOAD_SIPMOVIL_CHATS', dialogs);
          // commit('LOAD_RENDER_SIPMOVIL_CHATS', dialogs);
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async shareFexIAProject(_ctx, data) {
      return ChatService.shareFexIAProject(data);
    },
    async updateFexIAProject({ state }, data) {
      return ChatService.updateFexIAProject(data)
        .then((response) => {
          const updated = response.data;
          const chats = state.sipmovilChats.filter(
            (c) => c.isFexIA && Number(c.project_id) === Number(updated.project_id)
          );
          chats.forEach(chat => {
            chat.project = updated.name;
          });
          return updated;
        });
    },
    async selectFexIAChat({ commit, rootState }, chat) {
      const isEmojiOpen = rootState.ui.emojis.showEmojis;
      if (isEmojiOpen === true) {
        commit('ui/emojis/TOGGLE_EMOJI', null, { root: true });
      }
      commit('SET_SIPMOVIL_CHAT', chat);
      // dispatch('chat/updateUnread', chat.id, { root: true });
      commit(
        'ui/messagesOptions/ADD_MESSAGE_IN_PREVIEW',
        {
          value: false,
          msg: '',
        },
        { root: true }
      );
      commit('conversation/CLEAN_MESSAGES', null, { root: true });
      commit('conversation/UPDATE_MESSAGE', chat.draft ? chat.draft : '', { root: true });
      return 'Success';
    },
    async fetchSenders({ state, commit, dispatch, rootState }) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      const isAdmin = join.isAdmin;
      const owner = encryptStorage.getItem('user').Username;
      commit('SET_BLOCK_SCROLL', true);
      if (!isAdmin || !rootState.auth_store.userInfo.join.agrupedConversations) {
        return ChatService.getSenders(
          state.page,
          token,
          owner,
          isAdmin,
          state.cursor,
          state.orderByState
        )
          .then((response) => {
            commit('SET_BLOCK_SCROLL', false);
            commit(
              'SET_CHATS_TOTAL',
              parseInt(response.data.count, 10) // response.headers['x-total-count']
            );
            const dialogs = processSenders(response.data.results);
            if (state.page === 1) {
              commit('SET_CHATS', dialogs);
            } else {
              commit('LOAD_CHATS', dialogs);
            }
            commit('ui/customerOptions/SET_RENDER_CHATS', state.chats, { root: true });
            if (response.data.next !== null) {
              state.page += 1;
              const url = new URL(response.data.next);
              const params = Object.fromEntries(url.searchParams.entries());
              state.cursor = params.cursor;
            } else {
              state.page = 0;
              commit('SET_BLOCK_SCROLL', true);
            }
            dispatch('updateUnreadJoin');
            return 'Success';
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        commit('SET_BLOCK_SCROLL', true);
        for (const agent of rootState.conversation.transferAgents) {
          if (agent.page > 0) {
            ChatService.getChatsbyAgents(
              agent.userName,
              token,
              agent.page,
              state.agentsCursor,
              `&orderByState=${state.orderByState}`
            ) // user
              .then((response) => {
                commit('SET_BLOCK_SCROLL', false);
                commit(
                  'SET_CHATS_TOTAL',
                  parseInt(response.data.count, 10) // response.headers['x-total-count']
                );
                const dialogs = processSenders(response.data.results);
                commit('LOAD_CHATS', dialogs);
                commit('ui/customerOptions/SET_RENDER_CHATS', state.chats, { root: true });
                if (response.data.next !== null) {
                  agent.page += 1;
                  const url = new URL(response.data.next);
                  const params = Object.fromEntries(url.searchParams.entries());
                  state.agentsCursor = params.cursor;
                } else {
                  agent.page = 0;
                }
              });
          }
        }
      }
    },
    async filteredSenders({ state, commit, dispatch, rootState }, newPage = false) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      const isAdmin = join.isAdmin;
      const owner = encryptStorage.getItem('user').Username;
      const agroupedConv = rootState.auth_store.userInfo.join.agrupedConversations;
      const noReply = rootState.auth.onlyNoReply;
      let aditionalParams = `&noReply=${noReply}&unread=${state.filterUnread}&agroupedConv=${agroupedConv}&labels_id=${state.stringLabels}&orderByState=${state.orderByState}&archived=${state.archived}&banned=${state.banned}&origin=${state.selectedOrigins.join(',')}`;
      
      if (state.bulkDateFrom || state.bulkDateTo) {
        aditionalParams += `&date_criteria=${state.bulkDateCriteria || 'ninguno'}`;
        if (state.bulkDateFrom) aditionalParams += `&date_from=${state.bulkDateFrom}`;
        if (state.bulkDateTo) aditionalParams += `&date_to=${state.bulkDateTo}`;
      }
     
      if (state.transferDateFrom || state.transferDateTo) {
        aditionalParams += `&date_criteria=ultimo_mensaje`;
        if (state.transferDateFrom) aditionalParams += `&date_from=${state.transferDateFrom}`;
        if (state.transferDateTo) aditionalParams += `&date_to=${state.transferDateTo}`;
      }
      commit('SET_BLOCK_SCROLL', true);
      if (!agroupedConv || state.stringAgents) {
        return ChatService.getFilteredSenders(
          token,
          owner,
          isAdmin,
          state.page,
          state.filteredCursor,
          aditionalParams + `&agent=${state.stringAgents}`
        ) // user
          .then((response) => {
            commit('SET_BLOCK_SCROLL', false);
            commit(
              'SET_CHATS_TOTAL',
              parseInt(response.data.count, 10) // response.headers['x-total-count']
            );
            const senders = processSenders(response.data.results);
            if (newPage) {
              commit('LOAD_CHATS', senders);
            } else {
              commit('SET_CHATS', senders);
            }
            if (!newPage && state.pendingReminderNavigation) {
              const targetId = state.pendingReminderNavigation;
              commit('SET_PENDING_REMINDER_NAV', null);
              const target = senders.find((c) => c.id === targetId);
              if (target) dispatch('selectChat', target);
            }
            dispatch('updateUnreadJoin');
            dispatch('ui/toggleJoinLoading', false, { root: true })
            if (response.data.next !== null) {
              state.page += 1;
              const url = new URL(response.data.next);
              const params = Object.fromEntries(url.searchParams.entries());
              state.filteredCursor = params.cursor;
            } else {
              state.page = 0;
              commit('SET_BLOCK_SCROLL', true);
            }
            dispatch('ui/toggleJoinLoading', false, { root: true });
            dispatch('conversation/getStatusAgents', null, { root: true });
            return 'Success';
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        commit('SET_BLOCK_SCROLL', true);
        dispatch('conversation/getStatusAgents', null, { root: true }).then(() => {
          for (const agent of rootState.conversation.transferAgents) {
            if (agent.page > 0) {
              ChatService.getFilteredSenders(
                token,
                owner,
                join.isAdmin,
                agent.page,
                agent.filteredCursor,
                aditionalParams + `&agent=${agent.userName}`
              ) // user
                .then((response) => {
                  commit('SET_BLOCK_SCROLL', false);
                  commit(
                    'SET_CHATS_TOTAL',
                    parseInt(response.data.count, 10) // response.headers['x-total-count']
                  );
                  const dialogs = processSenders(response.data.results);
                  commit('LOAD_CHATS', dialogs);
                  dispatch('ui/toggleJoinLoading', false, { root: true });
                  dispatch('updateUnreadJoin');
                  if (response.data.next !== null) {
                    agent.page += 1;
                    const url = new URL(response.data.next);
                    const params = Object.fromEntries(url.searchParams.entries());
                    agent.filteredCursor = params.cursor;
                  } else {
                    agent.page = 0;
                  }
                  // console.log('AGENT PAGE:', agent);
                })
                .catch((error) => {
                  console.log(error);
                });
            }
          }
        });
      }
    },
    async fetchSipmovilSenders({ commit, dispatch }, toggle = true) {
      // const token = encryptStorage.getItem('token');

      if (toggle) dispatch('ui/toggleLoading', true, { root: true });
      return ChatService.getSipmovilSenders()
        .then((response) => {
          const dialogs = processSipmovilSenders(response.data.value);
          commit('LOAD_SIPMOVIL_CHATS', dialogs);
          commit('LOAD_SIPMOVIL_CHATS_FORDWARD', dialogs);
          commit('LOAD_RENDER_SIPMOVIL_CHATS', dialogs);
          dispatch('updateUnreadSipmovil');
          dispatch('fetchSipmovilStatus');
          dispatch('ui/toggleLoading', false, { root: true });
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
          dispatch('ui/toggleLoading', false, { root: true });
        });
    },
    async fetchSipmovilStatus({ commit, dispatch }) {
      return ChatService.getSipmovilStatusSenders()
        .then((response) => {
          for (const chat of response.data) {
            const data = {
              ...chat,
              endpoint: chat.account,
            };
            commit('UPDATE_SIPMOVIL_STATUS', { data, status: chat.status });
          }
        })
        .catch((error) => {
          console.log(error);
          dispatch('ui/toggleLoading', false, { root: true });
        });
    },
    fetchChat({ commit }, chat) {
      commit('LOAD_CHATS', processSenders(chat));
    },
    async reloadBot({ dispatch }) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      return ChatService.reloadBot(token) // user
        .then((response) => {
          console.log('Updateing bots response ', response);
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Operación exitosa',
              description: `Los bots han sido recargados`,
              alertType: 'success',
            },
            { root: true }
          );
          return 'Success';
        })
        .catch((error) => {
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Error',
              description: `Los bots no pudieron ser recargados`,
              alertType: 'error',
            },
            { root: true }
          );
          console.log(error);
        });
    },
    async updateUnread({ state, dispatch }, chatId) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      const chatIndex = state.chats.findIndex((x) => x.id === chatId, -1);
      const chat = state.chats[chatIndex];
      return ChatService.updateUnread(token, chat)
        .then(() => {
          dispatch('updateUnreadJoin');
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async getClosingTypes({ commit }) {
      return ChatService.getClosingTypes()
        .then((response) => {
          commit('LOAD_CLOSING_TYPES', response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async banChat({ state, commit }) {
      commit('contact/TOGGLE_LOADING', true, { root: true });
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      const user = encryptStorage.getItem('user').Username;
      return ChatService.banChat(token, state.openedChat, user)
        .then((response) => {
          commit('ui/modal/SET_TITLE', response.statusText, { root: true });
          commit('ui/modal/TOGGLE_MODAL', null, { root: true });
          commit('contact/TOGGLE_LOADING', false, { root: true });
          state.openedChat.blocked = true;
        })
        .catch((error) => {
          commit('ui/modal/SET_TITLE', error.response.data?.message, { root: true });
          commit('contact/TOGGLE_LOADING', false, { root: true });
          commit('ui/modal/SET_TARGET_ACTION', 'FINISHBLOCKACTION', { root: true });
        });
    },
    async unblockChat({ state, commit }) {
      commit('contact/TOGGLE_LOADING', true, { root: true });
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      const user = encryptStorage.getItem('user').Username;
      return ChatService.unblockChat(token, state.openedChat, user)
        .then((response) => {
          commit('ui/modal/SET_TITLE', response.statusText, { root: true });
          commit('ui/modal/TOGGLE_MODAL', null, { root: true });
          commit('contact/TOGGLE_LOADING', false, { root: true });
          state.openedChat.blocked = false;
        })
        .catch((error) => {
          commit('ui/modal/SET_TITLE', error.response.data?.message, { root: true });
          commit('contact/TOGGLE_LOADING', false, { root: true });
          commit('ui/modal/SET_TARGET_ACTION', 'FINISHBLOCKACTION', { root: true });
        });
    },
    async getPjsipContacts({ commit }, { data }) {
      // const token = encryptStorage.getItem('token');
      if (data.endpoint.endsWith('00')) {
        return ChatService.getPjsipContacts(data)
          .then((response) => {
            const data = response.data;
            const status = data.state;
            commit('UPDATE_SIPMOVIL_STATUS', { data, status });
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        const status = data.state;
        commit('UPDATE_SIPMOVIL_STATUS', { data, status });
      }
    },
    updateUnreadSipmovil({ state, commit }) {
      let total = 0;
      state.sipmovilChats.forEach((element) => {
        let unread = element.unread || 0;
        if (unread > 0 & !element.isFexIA) total += 1;
      });
      commit('UPDATE_UNREAD_SIPMOVIL', total);
    },
    updateUnreadJoin({ state, commit }) {
      let total = 0;
      state.chats.forEach((element) => {
        let unread = element.unread || 0;
        if (unread > 0) total += 1;
      });
      commit('UPDATE_UNREAD_JOIN', total);
    },
    sipmovilDeletedMessage({ state, rootState }, data) {
      const chatIndex = state.sipmovilChats.findIndex((x) => x.chatId == data.chatId, -1);
      const chat = state.sipmovilChats[chatIndex];
      // Marcar como eliminado en la caché del chat (chat.chats)...
      if (chat !== undefined && chat.chats !== undefined) {
        for (let day of chat.chats) {
          const msgIndex = day.data.findIndex((x) => x.id == data.id, -1);
          const msg = day.data[msgIndex];
          if (msg !== undefined) {
            msg.deleted = true;
          }
        }
      }
      // ...y SIEMPRE también en la conversación abierta (lo que se renderiza).
      // Antes esto estaba condicionado a no haberlo hallado en chat.chats, lo que
      // dejaba la burbuja visible sin actualizar cuando el mensaje existía en
      // ambos lados (entrar al chat tras recibir el mensaje y luego borrarlo).
      for (let day of rootState.conversation.messages) {
        const msgIndex = day.data.findIndex((x) => x.id == data.id, -1);
        const msg = day.data[msgIndex];
        if (msg !== undefined) {
          msg.deleted = true;
        }
      }
      if (chat !== undefined && data.id == chat.idLastMessage) {
        chat.deletedLastMessage = true;
      }
    },
    sipmovilEditedMessage({ state, rootState }, data) {
      const chatIndex = state.sipmovilChats.findIndex((x) => x.chatId == data.chatId, -1);
      const chat = state.sipmovilChats[chatIndex];
      // Actualizar en la caché del chat (chat.chats)...
      if (chat !== undefined && chat.chats !== undefined) {
        for (let day of chat.chats) {
          const msgIndex = day.data.findIndex((x) => x.id == data.id, -1);
          const msg = day.data[msgIndex];
          if (msg !== undefined) {
            msg.edited = true;
            msg.content = data.content;
            msg.message = data.content;
            // payload.text tiene prioridad en el render (messageGetter); si no se
            // actualiza, el mensaje propio muestra el texto viejo aunque cambie
            // message/content (solo aparece "Editado").
            if (msg.payload) msg.payload.text = data.content;
          }
        }
      }
      // ...y SIEMPRE también en la conversación abierta (lo que se renderiza).
      // Mismo fix que el borrado: antes se saltaba esto si ya lo había hallado en
      // chat.chats, dejando la burbuja visible sin actualizar el contenido/editado.
      for (let day of rootState.conversation.messages) {
        const msgIndex = day.data.findIndex((x) => x.id == data.id, -1);
        const msg = day.data[msgIndex];
        if (msg !== undefined) {
          msg.edited = true;
          msg.content = data.content;
          msg.message = data.content;
          if (msg.payload) msg.payload.text = data.content;
        }
      }
      if (chat !== undefined && data.id == chat.idLastMessage) {
        chat.last_message = data.content;
      }
    },
    async quitOfGroupRequest({ state, commit, dispatch }, id) {
      return ChatService.quitOfGroupRequest(id)
        .then((response) => {
          if (response.status === 200) {
            commit('UPDATE_SIPMOVIL_CHAT_ACTIVE_MEMBER', { chatId: id, isActiveMember: false, exitReason: 'left' });
            if (state.openedChat.chatId == id) {
              commit('conversation/UPDATE_PAGE', 1, { root: true });
              commit('conversation/CLEAN_MESSAGES', null, { root: true });
              commit('conversation/SET_ACTIVE_MEMBER', false, { root: true });
              commit('conversation/SET_EXIT_REASON', 'left', { root: true });
              dispatch('conversation/getSipmovilMessages', id, { root: true });
            }
            commit('pbxCloud_store/showSnackbarMessage', response.data.message, { root: true });
          } else {
            commit('pbxCloud_store/showSnackbarMessage', response.data.error, { root: true });
          }
          return 'Sucess';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    getSipmovilChat({ state }, chatData) {
      const chatIndex = state.renderSipmovilChats.findIndex((x) => x.chatId == chatData.chatId);
      return state.renderSipmovilChats[chatIndex];
    },
    async updatePinChat({ commit }, chatId) {
      const data = {
        chat_id: chatId,
      };
      return ChatService.updatePinChat(data)
        .then((response) => {
          const pinnedChats = (response.data?.pinnedChats || []).map(Number);
          commit('auth_store/SET_PINNED_CHATS', pinnedChats, { root: true });
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
        });
    },
    toggleChatFilterOnline({ commit }, status) {
      commit('TOGGLE_FILTER_CHAT_STATUS', status);
    },
    async endAgentConversations({ state, commit, dispatch, rootState }, agentName) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      return ChatService.endAgentConversations(agentName, token)
        .then(() => {
          // El backend emite "transferredChat" (state = closed) por cada
          // conversación cerrada; esto es respaldo por si algún evento no llega.
          state.chats
            .filter((chat) => chat.owner === agentName)
            .forEach((chat) => commit('REMOVE_CHATS', chat.id));
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Operación exitosa',
              description: `Se han finalizado las conversaciones del agente ${agentName}`,
              alertType: 'success',
            },
            { root: true }
          );
          const agent = rootState.conversation.transferAgents.filter((x) => x.userName === agentName)[0];
          if (agent) {
            agent.totalChats = 0;
          }
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Error',
              description:
                error.response?.data?.error ||
                `No se pudieron finalizar las conversaciones del agente ${agentName}`,
              alertType: 'error',
            },
            { root: true }
          );
        });
    },
    async transferAgentConversations({ commit, dispatch, rootState }, { currentOwner, newOwner }) {
      const join = encryptStorage.getItem('user').join;
      const token = join.tokenJoin;
      return ChatService.transferAgentConversations(currentOwner, newOwner, token)
        .then((response) => {
          commit('UPDATE_OWNER_CHATS', { currentOwner, newOwner });
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Operación exitosa',
              description: response.data.message || `Se transfirieron las conversaciones de ${currentOwner} a ${newOwner}`,
              alertType: 'success',
            },
            { root: true }
          );
          const agent = rootState.conversation.transferAgents.filter((x) => x.userName === currentOwner)[0];
          if (agent) {
            agent.totalChats = 0;
          }
          return 'Success';
        })
        .catch((error) => {
          console.log(error);
          dispatch(
            'ui/alert/displayAlert',
            {
              message: 'Error',
              description: error.response?.data?.error || `No se pudieron transferir las conversaciones de ${currentOwner} a ${newOwner}`,
              alertType: 'error',
            },
            { root: true }
          );
        });
    },
    async leaveAndDeleteGroupRequest({ commit, dispatch }, id) {
      return ChatService.leaveAndDeleteGroupRequest(id)
        .then((response) => {
          if (response.status === 200) {
            dispatch('removeSipmovilChatIfExists', id);
            commit('pbxCloud_store/showSnackbarMessage', response.data.message, { root: true });
          } else {
            commit('pbxCloud_store/showSnackbarMessage', response.data.error, { root: true });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    handleRemoveFromChat({ state, commit, dispatch }, { chat_id, reason, hidden }) {
      if (reason === 'deleted' || hidden) {
        dispatch('removeSipmovilChatIfExists', chat_id);
        return;
      }
      const exitReason = reason || null;
      commit('UPDATE_SIPMOVIL_CHAT_ACTIVE_MEMBER', { chatId: chat_id, isActiveMember: false, exitReason });
      if (state.openedChat.chatId == chat_id) {
        commit('conversation/UPDATE_PAGE', 1, { root: true });
        commit('conversation/CLEAN_MESSAGES', null, { root: true });
        commit('conversation/SET_ACTIVE_MEMBER', false, { root: true });
        commit('conversation/SET_EXIT_REASON', exitReason, { root: true });
        dispatch('conversation/getSipmovilMessages', chat_id, { root: true });
      }
    },
    handleAddToChat({ state, commit, dispatch }, { chat_id, chat_name, chat_avatar, is_group }) {
      const existing = state.sipmovilChats.find(c => c.chatId == chat_id);
      if (existing) {
        commit('UPDATE_SIPMOVIL_CHAT_ACTIVE_MEMBER', { chatId: chat_id, isActiveMember: true });
      } else {
        const user = encryptStorage.getItem('user');
        const newChat = {
          chatId: chat_id,
          id: chat_id,
          contact_dialog: chat_name || 'Grupo',
          img_dialog: chat_avatar ? `https://${user.PbxServer}${chat_avatar}` : '/static/images/group.png',
          isGroup: is_group ?? true,
          isActive: true,
          isActiveMember: true,
          last_message: '',
          last_message_status: 'read',
          last_message_incoming: false,
          date_message: '',
          unread: 0,
          state: 'online',
          chats: [],
          writing: false,
          description: '',
          onlyAdminsMessages: false,
        };
        commit('CREATE_SIPMOVIL_CHAT', newChat);
        commit('CREATE_RENDER_SIPMOVIL_CHAT', newChat);
      }
      const chatName = chat_name || existing?.contact_dialog || 'grupo';
      commit('ui/alert/DISPLAY_ALERT', {
        title: 'Agregado al grupo',
        message: `Te han agregado al grupo ${chatName}`,
        alertType: 'success'
      }, { root: true });
      if (state.openedChat.chatId == chat_id) {
        commit('conversation/UPDATE_PAGE', 1, { root: true });
        commit('conversation/CLEAN_MESSAGES', null, { root: true });
        dispatch('conversation/getSipmovilMessages', chat_id, { root: true });
      }
    },
    removeSipmovilChatIfExists({ state, commit }, chatId) {
      
      // Verificar si el chat existe en la lista - IMPORTANTE: buscar por chatId
      const chatIndex = state.sipmovilChats.findIndex((x) => x.chatId == chatId);
      
      
      if (chatIndex !== -1) {
        
        // El chat existe, lo removemos
        commit('REMOVE_SIPMOVIL_CHAT', chatId);
        
        // Si el chat removido era el que estaba abierto, cerrarlo
        if (state.openedChat.chatId == chatId) {
          commit('CLOSE_OPENED_CHATS');
          commit('conversation/CLEAN_MESSAGES', null, { root: true });
        }
        
        // Actualizar conteo de no leídos
        this.dispatch('chat/updateUnreadSipmovil');
        
        console.log('Chat removido exitosamente'); // DEBUG
      } else {
        console.warn('Chat no encontrado con ID:', chatId); // DEBUG
      }
    },
  },

  getters: {
    getSipmovilChats(state) {
      return state.sipmovilChats;
    },
  },
};

export default chat;
